// src/pages/support/HelpSupport.jsx
'use client'

import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  MagnifyingGlassIcon,
  ChevronDownIcon,
  XMarkIcon,
  Squares2X2Icon,
  BookOpenIcon,
  ChatBubbleLeftRightIcon,
  UserGroupIcon,
  UserCircleIcon,
  BellIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline'
import clsx from 'clsx'
import SupportRequestCard from '@/pages/support/SupportRequestCard'

const HEAD_FONT = "'Barlow Condensed', sans-serif"

/* ------------------------------------------------------------------ */
/* Categories — labels come from i18n (help.categories.*)              */
/* ------------------------------------------------------------------ */
const CATEGORIES = [
  { id: 'all',             key: 'all',             icon: Squares2X2Icon },
  { id: 'getting-started', key: 'gettingStarted',  icon: BookOpenIcon },
  { id: 'messaging',       key: 'messaging',       icon: ChatBubbleLeftRightIcon },
  { id: 'roster',          key: 'roster',          icon: UserGroupIcon },
  { id: 'account',         key: 'account',         icon: UserCircleIcon },
  { id: 'notifications',   key: 'notifications',   icon: BellIcon },
  { id: 'safety',          key: 'safety',          icon: ShieldCheckIcon },
]

/* FAQ index — content (q + a) lives in i18n under help.faqs.<id>.
   Keep this list in sync with the help.faqs keys in en.json / es.json. */
const FAQ_INDEX = [
  { id: 'whatIs',              category: 'getting-started' },
  { id: 'getStarted',          category: 'getting-started' },
  { id: 'addPlayersFirst',     category: 'getting-started' },
  { id: 'isFree',              category: 'account' },
  { id: 'messageTeam',         category: 'messaging' },
  { id: 'bilingual',           category: 'messaging' },
  { id: 'parentsReply',        category: 'messaging' },
  { id: 'parentsNeedApp',      category: 'messaging' },
  { id: 'addPlayers',          category: 'roster' },
  { id: 'inviteLinks',         category: 'roster' },
  { id: 'multipleTeams',       category: 'roster' },
  { id: 'profile',             category: 'account' },
  { id: 'deleteAccount',       category: 'account' },
  { id: 'manageNotifications', category: 'notifications' },
  { id: 'whatNotifications',   category: 'notifications' },
  { id: 'noPush',              category: 'notifications' },
  { id: 'whoSeesData',         category: 'safety' },
  { id: 'dataPrivate',         category: 'safety' },
  { id: 'reportProblem',       category: 'safety' },
]

/* ------------------------------------------------------------------ */
/* FAQ item                                                            */
/* ------------------------------------------------------------------ */
function FaqItem({ question, answer, isOpen, onClick }) {
  const isMultiline = typeof answer === 'string' && answer.includes('\n')

  return (
    <div
      className={clsx(
        'rounded-2xl border overflow-hidden transition-colors',
        isOpen
          ? 'bg-white border-[#b5f03d]/50 shadow-sm'
          : 'bg-white border-zinc-200 hover:border-zinc-300'
      )}
    >
      <button
        onClick={onClick}
        className="flex w-full items-center justify-between gap-3 px-4 sm:px-5 py-4 text-left focus:outline-none min-h-[44px]"
      >
        <span className={clsx(
          'text-[15px] font-semibold leading-snug',
          isOpen ? 'text-[#0d1f14]' : 'text-zinc-900'
        )}>
          {isOpen && <span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-[#b5f03d] align-middle" />}
          {question}
        </span>
        <ChevronDownIcon className={clsx(
          'h-5 w-5 shrink-0 text-zinc-400 transition-transform duration-300',
          isOpen && 'rotate-180 text-[#5a8a1f]'
        )} />
      </button>

      <div className={clsx(
        'grid transition-[grid-template-rows] duration-300 ease-out',
        isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
      )}>
        <div className="overflow-hidden">
          <div className="px-4 sm:px-5 pb-5">
            <div className="border-t border-zinc-100 pt-4 text-sm text-zinc-600 leading-relaxed">
              {isMultiline ? (
                <div className="space-y-2">
                  {String(answer).split('\n').map((line, idx) => {
                    const trimmed = line.trim()
                    if (!trimmed) return <div key={idx} className="h-1" />
                    const isBullet = /^[•\d]/.test(trimmed)
                    return (
                      <div key={idx} className={clsx(isBullet && 'pl-1')}>
                        {trimmed}
                      </div>
                    )
                  })}
                </div>
              ) : (
                <p>{answer}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Main page                                                           */
/* ------------------------------------------------------------------ */
export default function HelpSupport() {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')
  const [openId, setOpenId] = useState(null)
  const [activeCategory, setActiveCategory] = useState('all')

  // Resolve FAQ content from i18n once per render.
  const faqs = useMemo(
    () => FAQ_INDEX.map((f) => ({
      ...f,
      question: t(`help.faqs.${f.id}.q`),
      answer: t(`help.faqs.${f.id}.a`),
    })),
    [t]
  )

  const filteredFaqs = useMemo(() => {
    let result = faqs
    if (activeCategory !== 'all') {
      result = result.filter((f) => f.category === activeCategory)
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      result = result.filter(
        (f) =>
          f.question.toLowerCase().includes(q) ||
          String(f.answer).toLowerCase().includes(q)
      )
    }
    return result
  }, [faqs, searchQuery, activeCategory])

  const handleToggle = (id) => setOpenId(openId === id ? null : id)
  const handleCategory = (id) => {
    setActiveCategory(id)
    setOpenId(null)
  }
  const clearFilters = () => {
    setSearchQuery('')
    setActiveCategory('all')
    setOpenId(null)
  }

  return (
    <div className="bg-[#f7faf5] min-h-screen">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-6 sm:py-8">

        {/* Header */}
        <div className="mb-5">
          <h1
            className="uppercase text-[#0d1f14] leading-[1.05]"
            style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 30, letterSpacing: '0.4px' }}
          >
            {t('help.title')}
          </h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-xl">{t('help.subtitle')}</p>
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <MagnifyingGlassIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400 pointer-events-none" />
          <input
            type="text"
            placeholder={t('help.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-xl border border-zinc-200 bg-white pl-11 pr-10 py-3 text-sm text-zinc-900 placeholder:text-zinc-400 shadow-sm focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full bg-zinc-100 text-zinc-500 hover:bg-zinc-200 transition-colors"
            >
              <XMarkIcon className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Category chips — horizontal scroll on mobile */}
        <div className="mb-6 flex items-center gap-2 overflow-x-auto no-scrollbar -mx-1 px-1 pb-1">
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon
            const active = activeCategory === cat.id
            return (
              <button
                key={cat.id}
                onClick={() => handleCategory(cat.id)}
                className={clsx(
                  'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-2 text-xs font-semibold transition-colors shrink-0 border',
                  active
                    ? 'bg-[#0d1f14] text-[#b5f03d] border-[#0d1f14]'
                    : 'bg-white text-zinc-600 border-zinc-200 hover:border-[#b5f03d]/40'
                )}
              >
                <Icon className="h-4 w-4" />
                {t(`help.categories.${cat.key}`)}
              </button>
            )
          })}
        </div>

        {/* Result header */}
        <div className="flex items-center justify-between pb-2 mb-3 border-b border-zinc-200">
          <h2
            className="text-sm font-bold uppercase tracking-wide text-[#0d1f14]"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
          >
            {activeCategory === 'all'
              ? t('help.allQuestions')
              : t(`help.categories.${CATEGORIES.find((c) => c.id === activeCategory)?.key}`)}
          </h2>
          <span className="text-[11px] font-semibold text-zinc-500 bg-zinc-100 px-2 py-0.5 rounded-md">
            {filteredFaqs.length}
          </span>
        </div>

        {/* FAQ list */}
        {filteredFaqs.length > 0 ? (
          <div className="space-y-2.5">
            {filteredFaqs.map((faq) => (
              <FaqItem
                key={faq.id}
                question={faq.question}
                answer={faq.answer}
                isOpen={openId === faq.id}
                onClick={() => handleToggle(faq.id)}
              />
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border-2 border-dashed border-zinc-200 py-12 px-4 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#b5f03d]/15">
              <BookOpenIcon className="h-6 w-6 text-[#0d1f14]/60" />
            </div>
            <p
              className="uppercase text-[#0d1f14]"
              style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 18, letterSpacing: '0.4px' }}
            >
              {t('help.noResultsTitle')}
            </p>
            <p className="mt-1 text-sm text-zinc-500 max-w-sm mx-auto">{t('help.noResultsBody')}</p>
            <button
              onClick={clearFilters}
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold uppercase tracking-wide text-[#5a8a1f] hover:text-[#3d6010] transition-colors"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
            >
              {t('help.clearFilters')}
            </button>
          </div>
        )}

        {/* Still need help → Support request */}
        <div className="mt-10">
          <h2
            className="text-sm font-bold uppercase tracking-wide text-[#0d1f14] mb-3"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
          >
            {t('help.stillNeedHelp')}
          </h2>
          <SupportRequestCard />
        </div>
      </div>
    </div>
  )
}
